import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FaviconBadgeService {

  private readonly baseIconUrl =
    '/assets/images/svg/favicon.ico';

  private readonly size = 128;

  private baseImage: HTMLImageElement;
  private baseImageLoading: Promise<HTMLImageElement>;
  private canvas: HTMLCanvasElement;
  private linkEl: HTMLLinkElement;

  private lastCount: number = -1;
  private originalTitle: string;

  constructor() {
    // Save the original browser title
    this.originalTitle = document.title;
  }

  setCount(count: number): void {

    count = Math.max(0, Number(count) || 0);

    if (count === this.lastCount) {
      return;
    }

    this.lastCount = count;

    // Update browser tab title
    this.updateTitle(count);

    // Update favicon
    this.loadBaseImage()
      .then((img) => {
        this.render(img, count);
      })
      .catch((error) => {
        console.error('Unable to load favicon:', error);
      });
  }

  /**
   * Updates browser tab title
   *
   * Example:
   * (5) ServiceO
   * (25) ServiceO
   * ServiceO
   */
  private updateTitle(count: number): void {

    if (count > 0) {
      document.title = `(${count > 99
        ? '99+' : count}) ${this.originalTitle}`;
    } else {
      document.title = this.originalTitle;
    }
  }

  /**
   * Load original favicon
   */
  private loadBaseImage(): Promise<HTMLImageElement> {

    if (this.baseImage) {
      return Promise.resolve(this.baseImage);
    }

    if (this.baseImageLoading) {
      return this.baseImageLoading;
    }

    this.baseImageLoading = new Promise((resolve, reject) => {

      const img = new Image();

      img.onload = () => {
        this.baseImage = img;
        resolve(img);
      };

      img.onerror = (error) => {
        reject(error);
      };

      img.src = this.baseIconUrl;
    });

    return this.baseImageLoading;
  }

  /**
   * Draw favicon + notification badge
   */
  private render(
    img: HTMLImageElement,
    count: number
  ): void {

    if (!this.canvas) {

      this.canvas = document.createElement('canvas');

      this.canvas.width = this.size;
      this.canvas.height = this.size;
    }

    const ctx = this.canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    ctx.clearRect(
      0,
      0,
      this.size,
      this.size
    );

    // Draw original favicon
    ctx.drawImage(
      img,
      0,
      0,
      this.size,
      this.size
    );

    if (count > 0) {

      // const label = count > 99
      //   ? '99+'
      //   : String(count);

      const label = ''

      /*
       * Large badge.
       *
       * For 1-99:
       *     radius = 40
       *
       * For 99+:
       *     radius = 42
       */
      const radius = label.length > 2
        ? 42
        : 40;

      // Top-right position
      const cx = this.size - radius - 2;
      const cy = radius + 2;

      // Red notification circle
      ctx.beginPath();

      ctx.arc(
        cx,
        cy,
        radius,
        0,
        Math.PI * 2
      );

      ctx.fillStyle = '#ff0000';

      ctx.fill();

      // White border
      ctx.lineWidth = 6;
      ctx.strokeStyle = '#ffffff';

      ctx.stroke();

      // Notification number
      ctx.fillStyle = '#ffffff';

      ctx.font = label.length > 2
        ? 'bold 30px Arial'
        : 'bold 44px Arial';

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillText(
        label,
        cx,
        cy
      );
    }

    // Update browser favicon
    this.getLinkElement().href =
      this.canvas.toDataURL('image/png');
  }

  /**
   * Get/create favicon link element
   */
  private getLinkElement(): HTMLLinkElement {

    if (this.linkEl) {
      return this.linkEl;
    }

    let link = document.querySelector(
      'link[rel="icon"]'
    ) as HTMLLinkElement;

    if (!link) {

      link = document.createElement('link');

      link.rel = 'icon';
      link.type = 'image/png';

      document.head.appendChild(link);
    }

    this.linkEl = link;

    return link;
  }

  /**
   * Reset notification count
   */
  clear(): void {
    this.setCount(0);
  }
}